/**
 * SiteVisitsPage — Studio module for scheduling and recording project site
 * visits. Every scheduled visit is auto-visible on the client portal for that
 * project. Photos, discussion notes and next-action items are attached
 * post-visit.
 *
 * Also lists incoming client site-visit REQUESTS in a side inbox.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { http, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { CalendarBlank, Camera, Plus, X, CheckCircle } from "@phosphor-icons/react";

export default function SiteVisitsPage() {
  const { user } = useAuth();
  const isMgr = user?.role === "admin" || user?.role === "manager";
  const [visits, setVisits] = useState([]);
  const [requests, setRequests] = useState([]);
  const [projects, setProjects] = useState([]);
  const [members, setMembers] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [detailId, setDetailId] = useState(null);

  const load = useCallback(async () => {
    try {
      const [v, ps, ms] = await Promise.all([
        http.get("/site-visits"),
        isMgr ? http.get("/projects") : Promise.resolve({ data: [] }),
        isMgr ? http.get("/users?role=all") : Promise.resolve({ data: [] }),
      ]);
      setVisits(v.data || []);
      setProjects(ps.data || []);
      setMembers(ms.data || []);
      if (isMgr) {
        const r = await http.get("/site-visit-requests");
        setRequests(r.data || []);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }, [isMgr]);

  useEffect(() => { load(); }, [load]);

  const closeReq = async (id) => {
    try {
      await http.post(`/site-visit-requests/${id}/close`);
      toast.success("Request closed");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <Layout
      title="Site Visits"
      subtitle="Schedule, record and share every visit with the client."
      actions={isMgr ? (
        <Button
          onClick={() => setOpenNew(true)}
          data-testid="sv-new-btn"
          className="rounded-none bg-zinc-900 hover:bg-zinc-800 text-white font-mono uppercase tracking-widest text-xs gap-1.5"
        >
          <Plus size={12} weight="bold" /> Schedule Visit
        </Button>
      ) : null}
    >
      <div className="grid lg:grid-cols-[1fr_320px] gap-6">
        {/* Visits list */}
        <section data-testid="sv-list" className="space-y-3">
          {visits.length === 0 ? (
            <div className="border border-zinc-300 p-8 text-center text-sm text-zinc-500">
              No site visits yet. Schedule your first from the top-right button.
            </div>
          ) : visits.map((v) => (
            <VisitCard key={v.id} visit={v} onClick={() => setDetailId(v.id)} />
          ))}
        </section>

        {/* Client requests inbox (admin/PM only) */}
        {isMgr && (
          <aside data-testid="sv-requests" className="border border-zinc-300 bg-zinc-50 p-4 space-y-3 h-fit">
            <div className="label-overline text-zinc-500">Client requests</div>
            {requests.filter((r) => r.status === "new").length === 0 ? (
              <div className="text-xs text-zinc-500">No pending requests</div>
            ) : requests.filter((r) => r.status === "new").map((r) => (
              <div key={r.id} className="border border-zinc-300 bg-white p-3 text-xs space-y-1"
                   data-testid={`sv-req-${r.id}`}>
                <div className="flex items-baseline justify-between">
                  <div className="font-semibold">{r.client_name || "Client"}</div>
                  <div className="text-[10px] text-zinc-500">{r.project}</div>
                </div>
                {r.preferred_date && (
                  <div className="text-[11px] text-zinc-600">Wants: {r.preferred_date}</div>
                )}
                <div className="text-zinc-700 whitespace-pre-wrap">{r.message}</div>
                <div className="pt-1">
                  <button onClick={() => closeReq(r.id)}
                          data-testid={`sv-req-close-${r.id}`}
                          className="mono uppercase tracking-widest text-[10px] text-emerald-700 hover:underline">
                    Mark handled →
                  </button>
                </div>
              </div>
            ))}
          </aside>
        )}
      </div>

      {openNew && (
        <NewVisitDialog
          projects={projects}
          members={members}
          onClose={() => setOpenNew(false)}
          onSaved={() => { setOpenNew(false); load(); }}
        />
      )}

      {detailId && (
        <VisitDetail
          vid={detailId}
          isMgr={isMgr}
          onClose={() => setDetailId(null)}
          onChanged={load}
        />
      )}
    </Layout>
  );
}

// -----------------------------------------------------------------------------
function VisitCard({ visit, onClick }) {
  const badge =
    visit.status === "done" ? "bg-emerald-100 text-emerald-800 border-emerald-500" :
    visit.status === "cancelled" ? "bg-zinc-200 text-zinc-600 border-zinc-400" :
    "bg-amber-100 text-amber-800 border-amber-500";
  return (
    <button onClick={onClick}
            data-testid={`sv-card-${visit.id}`}
            className="block w-full text-left border border-zinc-300 bg-white p-4 hover:border-zinc-900 transition-colors">
      <div className="flex items-baseline justify-between gap-3">
        <div className="min-w-0">
          <div className="label-overline text-zinc-500">Job {visit.project}</div>
          <div className="font-display text-lg font-black tracking-tight truncate mt-1">
            {new Date(visit.scheduled_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })} IST
          </div>
        </div>
        <span className={`inline-block px-2 py-0.5 mono text-[10px] uppercase tracking-widest border ${badge}`}>
          {visit.status}
        </span>
      </div>
      {visit.attendee_names?.length > 0 && (
        <div className="text-xs text-zinc-600 mt-2 truncate">With: {visit.attendee_names.join(", ")}</div>
      )}
      <div className="flex items-center gap-3 mt-2 text-xs text-zinc-500">
        {visit.photos?.length > 0 && <span className="flex items-center gap-1"><Camera size={12} /> {visit.photos.length} photo{visit.photos.length > 1 ? "s" : ""}</span>}
        {visit.discussion && <span>· notes recorded</span>}
      </div>
    </button>
  );
}

// -----------------------------------------------------------------------------
function NewVisitDialog({ projects, members, onClose, onSaved }) {
  const [project, setProject] = useState("");
  const [scheduledAt, setScheduledAt] = useState("");
  const [attendeeIds, setAttendeeIds] = useState([]);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const toggleAttendee = (id) =>
    setAttendeeIds((cur) => cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!project || !scheduledAt) {
      toast.error("Project and scheduled date/time are required");
      return;
    }
    setBusy(true);
    try {
      const iso = new Date(scheduledAt).toISOString();
      await http.post("/site-visits", {
        project,
        scheduled_at: iso,
        attendee_ids: attendeeIds,
        notes,
      });
      toast.success("Site visit scheduled");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  const staff = members.filter((m) => ["admin", "manager", "member"].includes(m.role));

  return (
    <Dialog open={true} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="rounded-none border-2 border-zinc-900 shadow-brutal max-w-lg"
                     data-testid="sv-new-dialog">
        <DialogHeader>
          <div className="label-overline text-blue-600 mb-1">Schedule</div>
          <DialogTitle className="font-display text-2xl font-black tracking-tighter uppercase">
            New Site Visit
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-3 mt-3">
          <label className="block">
            <div className="label-overline text-zinc-500 mb-1">Project (Job No.) *</div>
            <Select value={project} onValueChange={setProject}>
              <SelectTrigger className="rounded-none border-zinc-400" data-testid="sv-new-project">
                <SelectValue placeholder="Select project" />
              </SelectTrigger>
              <SelectContent className="rounded-none">
                {projects.map((p) => (
                  <SelectItem key={p.name} value={p.name} className="rounded-none">
                    {p.name}{p.title ? ` · ${p.title}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
          <label className="block">
            <div className="label-overline text-zinc-500 mb-1">When *</div>
            <Input type="datetime-local" value={scheduledAt}
                   onChange={(e) => setScheduledAt(e.target.value)}
                   data-testid="sv-new-when"
                   className="rounded-none border-zinc-400" required />
          </label>
          <div>
            <div className="label-overline text-zinc-500 mb-1">Attendees (studio side)</div>
            <div className="border border-zinc-300 bg-zinc-50 p-2 max-h-40 overflow-y-auto space-y-1">
              {staff.length === 0 ? (
                <div className="text-xs text-zinc-500 p-1">No team members loaded</div>
              ) : staff.map((s) => (
                <label key={s.id} className="flex items-center gap-2 text-sm hover:bg-white px-2 py-1 cursor-pointer">
                  <input type="checkbox" checked={attendeeIds.includes(s.id)}
                         onChange={() => toggleAttendee(s.id)}
                         data-testid={`sv-new-attendee-${s.id}`} />
                  {s.name} <span className="text-xs text-zinc-500">· {s.role}</span>
                </label>
              ))}
            </div>
          </div>
          <label className="block">
            <div className="label-overline text-zinc-500 mb-1">Pre-visit notes / agenda</div>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                      data-testid="sv-new-notes"
                      className="w-full rounded-none border border-zinc-400 p-3 text-sm min-h-[80px]" />
          </label>
          <div className="flex justify-end gap-2 pt-3 border-t border-zinc-300">
            <Button type="button" variant="outline" onClick={onClose}
                    className="rounded-none border-zinc-400 font-mono uppercase tracking-widest text-xs">Cancel</Button>
            <Button type="submit" disabled={busy}
                    data-testid="sv-new-submit"
                    className="rounded-none bg-emerald-700 hover:bg-emerald-800 text-white font-mono uppercase tracking-widest text-xs px-5">
              <CalendarBlank size={12} weight="bold" className="mr-1.5" />
              {busy ? "Scheduling…" : "Schedule"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// -----------------------------------------------------------------------------
function VisitDetail({ vid, isMgr, onClose, onChanged }) {
  const [v, setV] = useState(null);
  const [busy, setBusy] = useState(false);
  const [discussion, setDiscussion] = useState("");
  const [nextActions, setNextActions] = useState("");
  const fileRef = useRef();
  const [caption, setCaption] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await http.get(`/site-visits/${vid}`);
      setV(data);
      setDiscussion(data.discussion || "");
      setNextActions(data.next_actions || "");
    } catch (err) { toast.error(formatApiError(err)); onClose(); }
  }, [vid, onClose]);

  useEffect(() => { load(); }, [load]);

  const save = async (patch) => {
    setBusy(true);
    try {
      await http.patch(`/site-visits/${vid}`, patch);
      toast.success("Saved");
      await load();
      onChanged?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("caption", caption);
      await http.post(`/site-visits/${vid}/photos`, fd);
      toast.success("Photo added");
      setCaption("");
      await load();
      onChanged?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); e.target.value = ""; }
  };

  const removePhoto = async (pid) => {
    if (!window.confirm("Delete this photo?")) return;
    try {
      await http.delete(`/site-visits/${vid}/photos/${pid}`);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const dead = async () => {
    if (!window.confirm("Delete this visit? This can't be undone.")) return;
    try {
      await http.delete(`/site-visits/${vid}`);
      toast.success("Deleted");
      onChanged?.();
      onClose();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (!v) return null;

  return (
    <Dialog open={true} onOpenChange={(x) => { if (!x) onClose(); }}>
      <DialogContent className="rounded-none border-2 border-zinc-900 shadow-brutal max-w-3xl max-h-[90vh] overflow-y-auto"
                     data-testid={`sv-detail-${vid}`}>
        <DialogHeader>
          <div className="flex items-baseline justify-between gap-3">
            <div>
              <div className="label-overline text-zinc-500">Job {v.project}</div>
              <DialogTitle className="font-display text-2xl font-black tracking-tighter uppercase">
                {new Date(v.scheduled_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })} IST
              </DialogTitle>
              {v.attendee_names?.length > 0 && (
                <div className="text-xs text-zinc-600 mt-1">With: {v.attendee_names.join(", ")}</div>
              )}
            </div>
            {isMgr && (
              <Select value={v.status} onValueChange={(s) => save({ status: s })}>
                <SelectTrigger className="rounded-none border-zinc-400 w-32" data-testid="sv-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="upcoming">Upcoming</SelectItem>
                  <SelectItem value="done">Done</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {v.notes && (
            <div>
              <div className="label-overline text-zinc-500 mb-1">Pre-visit agenda</div>
              <div className="text-sm whitespace-pre-wrap text-zinc-700">{v.notes}</div>
            </div>
          )}

          <label className="block">
            <div className="label-overline text-zinc-500 mb-1">What was discussed / observed</div>
            <textarea value={discussion} onChange={(e) => setDiscussion(e.target.value)}
                      disabled={!isMgr}
                      data-testid="sv-discussion"
                      className="w-full rounded-none border border-zinc-400 p-3 text-sm min-h-[120px] bg-white disabled:bg-zinc-50" />
          </label>
          <label className="block">
            <div className="label-overline text-zinc-500 mb-1">Next actions</div>
            <textarea value={nextActions} onChange={(e) => setNextActions(e.target.value)}
                      disabled={!isMgr}
                      data-testid="sv-next"
                      className="w-full rounded-none border border-zinc-400 p-3 text-sm min-h-[80px] bg-white disabled:bg-zinc-50" />
          </label>

          {isMgr && (
            <Button onClick={() => save({ discussion, next_actions: nextActions })}
                    disabled={busy}
                    data-testid="sv-save"
                    className="rounded-none bg-zinc-900 hover:bg-zinc-800 text-white font-mono uppercase tracking-widest text-xs">
              <CheckCircle size={12} weight="bold" className="mr-1.5" /> Save Notes
            </Button>
          )}

          <div>
            <div className="label-overline text-zinc-500 mb-2 flex items-center justify-between">
              <span>Site photos ({v.photos?.length || 0})</span>
              {isMgr && (
                <div className="flex items-center gap-2">
                  <Input value={caption} onChange={(e) => setCaption(e.target.value)}
                         placeholder="Caption (optional)"
                         data-testid="sv-photo-caption"
                         className="rounded-none border-zinc-400 h-8 text-xs w-56" />
                  <input type="file" hidden accept="image/*" ref={fileRef} onChange={upload}
                         data-testid="sv-photo-file" />
                  <Button size="sm" onClick={() => fileRef.current?.click()}
                          data-testid="sv-photo-upload"
                          className="rounded-none bg-zinc-900 hover:bg-zinc-800 text-white font-mono uppercase tracking-widest text-xs h-8">
                    <Camera size={12} weight="bold" className="mr-1.5" /> Add
                  </Button>
                </div>
              )}
            </div>
            {(!v.photos || v.photos.length === 0) ? (
              <div className="border border-dashed border-zinc-300 p-6 text-center text-xs text-zinc-500">No photos yet</div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {v.photos.map((p) => (
                  <div key={p.id} className="relative border border-zinc-300 group"
                       data-testid={`sv-photo-${p.id}`}>
                    <img src={p.url} alt={p.caption || ""} className="w-full aspect-[4/3] object-cover" />
                    {p.caption && (
                      <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[10px] p-1 truncate">{p.caption}</div>
                    )}
                    {isMgr && (
                      <button onClick={() => removePhoto(p.id)}
                              data-testid={`sv-photo-del-${p.id}`}
                              className="absolute top-1 right-1 bg-white border border-zinc-400 p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <X size={10} weight="bold" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {isMgr && (
            <div className="pt-4 border-t border-zinc-200">
              <button onClick={dead}
                      data-testid="sv-delete"
                      className="text-xs text-red-600 hover:underline">Delete this visit</button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
